import { ComponentPropsWithoutRef, FC, useState } from 'react';
import Cropper, { Area, Point } from 'react-easy-crop';
import { useDebouncedCallback } from 'use-debounce';
import { Box, Stack } from '@mui/material';
import { Button, FileInput } from 'melp-design/components';
import { Close } from '@mui/icons-material';
import { Filetype } from 'store/files';
import { getCroppedImg, readFile } from './image-crop-input.utils';

type Props = {
  aspectRatio?: [number, number];
  accept?: Extract<
    Filetype,
    'jpg' | 'jpeg' | 'png' | 'avif' | 'gif' | 'bmp' | 'webp'
  >[];
  onChange: (image: Blob | null) => void;
} & Pick<
  ComponentPropsWithoutRef<typeof FileInput>,
  'name' | 'maxSize' | 'disabled' | 'required' | 'error'
>;

export const ImageCropInput: FC<Props> = ({
  aspectRatio = [1, 1],
  accept,
  onChange,
  ...fileInputProps
}) => {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [imageMime, setImageMime] = useState<string | null>(null);

  const onCropComplete = useDebouncedCallback(
    async (_croppedArea: Area, croppedAreaPixels: Area) => {
      if (!croppedAreaPixels || !imageSrc) return;

      const croppedImage = await getCroppedImg({
        imageSrc,
        pixelCrop: croppedAreaPixels,
        mime: imageMime || undefined,
      });

      if (croppedImage) {
        onChange(croppedImage.blob);
      }
    },
    300,
  );

  const onFileChange = async (files: File[]) => {
    const [file] = files;
    if (file) {
      const imageDataUrl = await readFile(file);
      setImageSrc(imageDataUrl);
      setImageMime(file.type);
    }
  };

  const onClear = () => {
    setImageSrc(null);
    setImageMime(null);
    onChange(null);
  };

  return (
    <Stack gap={2}>
      {imageSrc ? (
        <Box
          sx={{
            width: '100%',
            aspectRatio: aspectRatio.join('/'),
            position: 'relative',
          }}
        >
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspectRatio[0] / aspectRatio[1]}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
          />

          <Box sx={{ position: 'absolute', top: 5, right: 5 }}>
            <Button
              variant="neutral-fill"
              size="sm"
              icon={Close}
              onClick={onClear}
            />
          </Box>
        </Box>
      ) : (
        <FileInput
          {...fileInputProps}
          onChange={onFileChange}
          accept={accept}
          maxFiles={1}
          previewSelectedFiles={false}
        />
      )}
    </Stack>
  );
};
